import { checkHandleFormat, handleTarget } from '@guildite/domain/handles';
import type { PublicShopLocation, ShopLocationSettings } from '@guildite/domain/location';

import { toPublicShop } from './adapters/shop.ts';
import type { ExactShopLocation, MyShop, OrderAreas, ShopInvite, ShopLocationSettingsView, ShopLookup, ShopsPort, ShopTeam, ShopTransfer } from './ports.ts';
import type { WorkerClient } from './worker.ts';

type ShopRecordsReader = (shopId: string) => Promise<{
  shop: Record<string, unknown> | null;
  legacyProfile: Record<string, unknown> | null;
}>;

const SHOP_ID = /^[A-Za-z0-9_-]{1,128}$/;

export function createShops({ worker, readShopRecords, readHandleRecord, now = () => Date.now() }: {
  worker: WorkerClient;
  readShopRecords: ShopRecordsReader;
  readHandleRecord: (handle: string) => Promise<Record<string, unknown> | null>;
  now?: () => number;
}): ShopsPort {
  async function shopById(shopId: string, requested: string): Promise<ShopLookup> {
    const { shop, legacyProfile } = await readShopRecords(shopId);
    const view = toPublicShop(shopId, shop, legacyProfile);
    if (!view) return { status: 'missing' };
    return { status: 'found', shop: view, redirectTo: view.handle && view.handle !== requested ? view.handle : null };
  }

  return {
    // /s/<value>: a handle (current, or an old one still redirecting) first;
    // otherwise an old shop id. Both lead to the shop's current handle.
    async lookupShop(value) {
      const requested = value.trim();
      const format = checkHandleFormat(requested);
      if (format.ok) {
        const target = handleTarget(await readHandleRecord(format.handle), now());
        if (target?.type === 'shop') return shopById(target.id, requested);
      }
      return SHOP_ID.test(requested) ? shopById(requested, requested) : { status: 'missing' };
    },
    startTransfer: async (shopId, to, afterwards, note) => (await worker.call<{ transfer: ShopTransfer }>('start_shop_transfer', { shopId, ...to, afterwards, note })).transfer,
    cancelTransfer: async shopId => { await worker.call('cancel_shop_transfer', { shopId }); },
    getTransfer: async shopId => (await worker.call<{ transfer: ShopTransfer | null }>('get_shop_transfer', { shopId })).transfer,
    myTransfers: async () => (await worker.call<{ transfers: ShopTransfer[] }>('list_my_shop_transfers')).transfers,
    respondToTransfer: (shopId, accept) => worker.call('respond_shop_transfer', { shopId, accept }),
    orderAreas: (shopId, days) => worker.call<OrderAreas>('shop_order_areas', { shopId, days }),
    getMyShops: async () => (await worker.call<{ shops: MyShop[] }>('get_my_shops')).shops.map(({ payoutStatus, ...shop }) => ({
      ...shop, ...(payoutStatus === 'ready' || payoutStatus === 'pending' || payoutStatus === 'none' ? { payoutStatus } : {}),
    })),
    createShop: async input => (await worker.call<{ shop: { shopId: string; name: string; handle: string | null } }>('create_shop', input)).shop,
    async changeHandle(shopId, handle) {
      const result = await worker.call<{ shop: { handle: string }; previousHandle: string | null }>('change_shop_handle', { shopId, handle });
      return { handle: result.shop.handle, previousHandle: result.previousHandle };
    },
    changeMemberRole: async (shopId, uid, role) => { await worker.call('change_member_role', { shopId, uid, role }); },
    getTeam: shopId => worker.call<ShopTeam>('list_shop_team', { shopId }),
    invite: async (shopId, to, role) => (await worker.call<{ invite: ShopInvite }>('invite_shop_member', { shopId, ...to, role })).invite,
    revokeInvite: async inviteId => { await worker.call('revoke_shop_invite', { inviteId }); },
    removeMember: async (shopId, uid) => { await worker.call('remove_shop_member', { shopId, uid }); },
    leaveShop: async shopId => { await worker.call('leave_shop', { shopId }); },
    closeShop: async (shopId, confirmHandle) => { await worker.call('close_shop', { shopId, confirmHandle }); },
    getMyInvites: async () => (await worker.call<{ invites: ShopInvite[] }>('list_my_shop_invites')).invites,
    respondToInvite: (inviteId, accept) => worker.call('respond_shop_invite', { inviteId, accept }),
    getLocationSettings: shopId => worker.call<ShopLocationSettingsView>('get_shop_location_settings', { shopId }),
    async saveLocation(shopId: string, settings: ShopLocationSettings, mode?: 'physical' | 'home') {
      const result = await worker.call<{ location: PublicShopLocation }>('save_shop_location', { shopId, location: settings, ...(mode ? { mode } : {}) });
      return result.location;
    },
    getExactLocation: (shopId, orderId) => worker.call<ExactShopLocation>('get_shop_exact_location', { shopId, ...(orderId ? { orderId } : {}) }),
  };
}
